/* eslint-disable camelcase */
import config from '../config/google'
import { getFirstGroupedNumbersIfExist } from '../libs/strings'
import { getCurrentLanguage } from '../translations/i18n'
import { MapsAPIGeocodeResponse, Location } from '../types/places'

interface GetLocationInfoOptions {
  streetNumberOverride?: string
}

const getApiUrlLanguage = () => {
  const language = getCurrentLanguage()
  return `${language}-${language.toUpperCase()}`
}

const getPlacesApiUrl = () => {
  const lang = getApiUrlLanguage()
  // Load Maps JS API asynchronously (recommended). Also request places library.
  // See https://goo.gle/js-api-loading
  return `https://maps.googleapis.com/maps/api/js?key=${config.googleMaps.places.API_KEY}` +
    `&language=${lang}` +
    `&v=weekly` +
    `&loading=async` +
    `&libraries=places`
}

const buildAddressQuery = (label: string, streetNumber: string) => {
  const parts = label.split(',').map((part) => part.trim())
  const [street = '', ...rest] = parts
  const baseStreet = streetNumber ? `${street} ${streetNumber}`.trim() : street
  const restJoined = rest.filter(Boolean).join(', ')
  return restJoined ? `${baseStreet}, ${restJoined}` : baseStreet
}

const getLocationInfo = async (
  placeId: string,
  label: string,
  options: GetLocationInfoOptions = {}
) => {
  const streetNumberOverride = options.streetNumberOverride?.trim()

  const addressQuery = streetNumberOverride
    ? buildAddressQuery(label, streetNumberOverride)
    : undefined

  const url = addressQuery
    ? `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressQuery)}&key=${config.googleMaps.places.API_KEY}`
    : `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${config.googleMaps.places.API_KEY}`

  let locationResponse: MapsAPIGeocodeResponse = await fetch(url).then((response) =>
    response.json()
  )

  if (!locationResponse.results.length && addressQuery) {
    locationResponse = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${config.googleMaps.places.API_KEY}`
    ).then((response) => response.json())
  }

  if (!locationResponse.results.length) {
    throw new Error('Google geocode returned no results')
  }

  const {
    place_id,
    address_components,
    formatted_address,
    geometry
  } = locationResponse.results[0]

  const streetNumberFromGoogle =
    address_components.find(({ types }) => types.includes('street_number'))
      ?.long_name || undefined

  const streetNumber =
    streetNumberOverride ||
    streetNumberFromGoogle ||
    getFirstGroupedNumbersIfExist(label.split(',')[0])
  const streetWithoutNumber = formatted_address.split(',')[0]
  const streetAddress = streetNumber
    ? `${streetWithoutNumber} ${streetNumber}`
    : streetWithoutNumber

  const location: Location = {
    googlePlaceId: place_id,
    streetAddress,
    postalCode: address_components.find(({ types }) =>
      types.includes('postal_code')
    )?.long_name,
    postalArea: address_components.find(
      ({ types }) =>
        types.includes('locality') ||
        types.includes('administrative_area_level_3')
    )?.long_name,
    coordinates: geometry.location
  }
  return location as Location
}

export { getLocationInfo, getPlacesApiUrl }
